import { Block, Edit, Send, Visibility } from '@mui/icons-material';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { MRT_ColumnDef as MRTColumnDef } from 'material-react-table';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { formatLocalDate } from '../../../../app/utils';
import { useAppSelector } from '../../../../app/hooks';
import { useNotificationStack } from '../../../../app/hooks/useNotificationStack';
import {
    useGetUserTypesQuery,
    useGetUsersQuery,
    useGetUsersPagedQuery,
    useReissueInvitationMutation,
    useRevokeInvitationMutation,
} from '../../../../app/redux-fetch/apiQuery';
import AddButton from '../../../../components/Buttons/AddButton';
import ConditionalRender from '../../../../components/ConditionalRender/ConditionalRender';
import MRTDataGridv2 from '../../../../components/MRTDataGrid/MRTDataGridv2';
import SideModalWrapper from '../../../Assets/modals/SideModalWrapper';
import { fetchLoggedInUserSelector } from '../../../selectors';
import { ILoggedInUser } from '../../../types';
import { IUser, IUserListItem, UserStatus } from '../types';
import CreateUserModal from './CreateUserModal';
import InviteUserModal from './InviteUserModal';
import UserModal from './UserModal';
import messages from './messages';

// E10 F10-7. A grid row is the existing rich IUser enriched with the computed invitation lifecycle fields
// (merged by id from the paged users-list read).
type UserRow = IUser & {
    formattedLastUpdated?: string
    status?: UserStatus
    invitationId?: number | null
}

const STATUS_COLOR: Record<UserStatus, 'success' | 'info' | 'warning' | 'default'> = {
    Active: 'success',
    Invited: 'info',
    Expired: 'warning',
    Disabled: 'default',
};

const UserTable = () => {
    const { formatMessage } = useIntl()
    const { enqueueSuccess } = useNotificationStack()

    const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)
    const tenantId = loggedInUser.tenantId
    const customerId = loggedInUser.customerId
    // E10 F10-7 is tenant-scoped: usp_GetUsersForTenant + the invite backend key off the caller's Tenant
    // (CustomerId IS NULL). A customer-scoped view (customerId set) lists customer users, which the tenant
    // paged endpoint does not cover — so the Status column + invite features only apply at tenant scope.
    const isTenantScope = !customerId

    const { data: fetchedUsers, isSuccess: userLoaded } = useGetUsersQuery({
        tenantId,
        customerId
    }, { refetchOnMountOrArgChange: true }
    );

    const { isSuccess: userTypeLoaded } = useGetUserTypesQuery({
        tenantId
    })

    // E10 F10-7: the computed lifecycle Status + the live Pending invitation handle for each tenant user,
    // merged into the rich rows by id. Same scope as getUsers' tenant path, so the rows align. pageSize 200
    // covers a tenant's staff in one page; rows beyond that simply show no Status until a "load more" is
    // added (a tenant with >200 staff is not an E10 concern).
    const { data: pagedUsers } = useGetUsersPagedQuery(
        { tenantId, pageSize: 200 },
        { skip: !tenantId || !isTenantScope, refetchOnMountOrArgChange: true }
    );

    const statusById = useMemo(() => {
        const map = new Map<number, IUserListItem>()
        pagedUsers?.items?.forEach((item) => map.set(item.id, item))
        return map
    }, [pagedUsers]);

    const [reissueInvitation] = useReissueInvitationMutation()
    const [revokeInvitation] = useRevokeInvitationMutation()

    const users = useMemo<UserRow[]>(() => {
        return fetchedUsers?.map((user: IUser) => {
            const listItem = user.id != null ? statusById.get(user.id) : undefined
            return {
                ...user,
                formattedLastUpdated: formatLocalDate(user.lastUpdated),
                status: listItem?.status,
                invitationId: listItem?.invitationId,
            }
        }) ?? [];
    }, [fetchedUsers, statusById]);

    const statusLabel = (status?: UserStatus) => {
        switch (status) {
            case 'Active': return formatMessage(messages.statusActive)
            case 'Invited': return formatMessage(messages.statusInvited)
            case 'Expired': return formatMessage(messages.statusExpired)
            case 'Disabled': return formatMessage(messages.statusDisabled)
            default: return ''
        }
    }

    const columns = useMemo<MRTColumnDef<UserRow>[]>(() => {
        return [
            {
                accessorKey: 'firstName',
                header: formatMessage(messages.firstNameLabel),
            },
            {
                accessorKey: 'lastName',
                header: formatMessage(messages.lastNameLabel),
            },
            {
                accessorKey: 'email',
                header: formatMessage(messages.emailLabel),
            },
            // E10 F10-7 Status column — tenant scope only (the paged endpoint that feeds it is tenant-only).
            ...(isTenantScope ? [{
                accessorKey: 'status',
                header: formatMessage(messages.statusLabel),
                Cell: ({ row }: any) => {
                    const status = row.original.status as UserStatus | undefined
                    if (!status) return null
                    return <Chip size='small' color={STATUS_COLOR[status]} label={statusLabel(status)} />
                },
            }] as MRTColumnDef<UserRow>[] : []),
            {
                accessorKey: 'userTypeId',
                header: formatMessage(messages.userType),
                Cell: ({ cell }: any) => {
                    return cell.getValue() === 0 ? formatMessage(messages.systemUserLabel) : formatMessage(messages.workforceUserLabel)
                }
            },
            {
                accessorKey: 'phone',
                header: formatMessage(messages.phoneLabel),
            },
            {
                accessorKey: 'disabled',
                header: formatMessage(messages.activeLbl),
                Cell: ({ row }: any) => {

                    return !row.original.disabled ? (
                        <Typography sx={{ fontWeight: 700, color: '#0A9D33' }}>{formatMessage(messages.yesLabel)}</Typography>
                    ) : (
                        <Typography sx={{ fontWeight: 700, color: '#D40808' }}>{formatMessage(messages.noLabel)}</Typography>
                    );
                },
            },
        ];
    }, [isTenantScope]);



    const [createOpen, setCreateOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [viewOpen, setViewOpen] = useState(false);

    const [selectedUserId, setSelectedUserId] = useState<string | undefined>();

    const onResend = async (userId: number) => {
        try {
            await reissueInvitation({ userId }).unwrap()
            enqueueSuccess(messages.resendInviteSuccess)
        } catch {
            // the shared base-query toast already surfaced the error
        }
    }

    const onRevoke = async (invitationId: number) => {
        if (!window.confirm(formatMessage(messages.revokeInviteConfirm))) return
        try {
            await revokeInvitation({ invitationId }).unwrap()
            enqueueSuccess(messages.revokeInviteSuccess)
        } catch {
            // the shared base-query toast already surfaced the error
        }
    }

    return (<>
        <SideModalWrapper
            open={createOpen}
            key={'create'}
            headerLabel={formatMessage(messages.createUserLabel)}
            onClose={() => {
                setCreateOpen((pv) => {
                    return !pv
                })
            }}
            withBottomBox={false}
        >
            <CreateUserModal />
        </SideModalWrapper>
        <SideModalWrapper
            open={inviteOpen}
            key={'invite'}
            headerLabel={formatMessage(messages.inviteUserButton)}
            onClose={() => {
                setInviteOpen((pv) => {
                    return !pv
                })
            }}
            withBottomBox={false}
        >
            <InviteUserModal />
        </SideModalWrapper>
        <SideModalWrapper
            open={editOpen}
            key={'edit'}
            headerLabel={formatMessage(messages.editUserLabel)}
            onClose={() => {
                setEditOpen((pv) => {
                    return !pv
                })
            }}
            withBottomBox={false}
        >
            <UserModal userId={selectedUserId} />
        </SideModalWrapper>
        <SideModalWrapper
            open={viewOpen}
            key={'view'}
            headerLabel={formatMessage(messages.viewUserLabel)}
            onClose={() => {
                setViewOpen((pv) => {
                    return !pv
                })
            }}
            withBottomBox={false}
        >
            <UserModal userId={selectedUserId} isDisabled={true} />
        </SideModalWrapper>
        <MRTDataGridv2
            initialState={{
                sorting: [
                    {
                        id: 'firstName',
                        desc: false,
                    },
                    {
                        id: 'lastName',
                        desc: false
                    }]
            }}
            state={{
                isLoading: !userLoaded || !userTypeLoaded,
            }}
            rightHeadingComponent={
                <Box sx={{ display: 'flex', gap: '8px' }}>
                    {/* Invite is tenant-scoped (the invite backend keys off the caller's Tenant persona). */}
                    {isTenantScope && (
                        <AddButton
                            text={formatMessage(messages.inviteUserButton)}
                            onClick={() => setInviteOpen(true)}
                        />
                    )}
                    <AddButton
                        text={formatMessage(messages.newUserLabel)}
                        onClick={() => setCreateOpen(true)}
                    />
                </Box>
            }
            columns={columns}
            data={users || []}
            getRowId={(row) => row.id as any}
            enableEditing
            enableRowActions
            positionActionsColumn='last'
            renderRowActions={({ row }: any) => {
                const status = row.original.status as UserStatus | undefined
                const invitationId = row.original.invitationId as number | null | undefined
                const canResend = status === 'Invited' || status === 'Expired'
                const canRevoke = status === 'Invited' && invitationId != null
                return (
                    <Box sx={{ display: 'flex' }}>
                        <ConditionalRender condition={row.original.userTypeId === 0}>
                            <IconButton
                                color={'primary'}
                                onClick={() => {
                                    setSelectedUserId(row.id)
                                    setViewOpen(true)
                                }}
                            >
                                <Visibility />
                            </IconButton>
                            <IconButton
                                color={'primary'}
                                onClick={() => {
                                    setSelectedUserId(row.id)
                                    setEditOpen(true)
                                }}
                            >
                                <Edit />
                            </IconButton>
                        </ConditionalRender>
                        <ConditionalRender condition={canResend}>
                            <Tooltip title={formatMessage(messages.resendInvite)}>
                                <IconButton color={'primary'} onClick={() => onResend(row.original.id)}>
                                    <Send />
                                </IconButton>
                            </Tooltip>
                        </ConditionalRender>
                        <ConditionalRender condition={canRevoke}>
                            <Tooltip title={formatMessage(messages.revokeInvite)}>
                                <IconButton color={'error'} onClick={() => onRevoke(invitationId as number)}>
                                    <Block />
                                </IconButton>
                            </Tooltip>
                        </ConditionalRender>
                    </Box>
                )
            }}
        />
    </>
    );
};

export default UserTable;
